import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useUnit } from "effector-react";
import { authenticationStore } from "@state/auth/AuthStore";
import OfficeIcon from "@static/core/icons/icon-office.svg?react";
import UserIcon from "@static/core/icons/icon-user.svg?react";
import { ROUTES } from "@routes/Routes";
import useUserPicture from "@/utils/hooks/useUserPicture";
import { getBase64UrlFrom } from "@/utils/helpers/base64-helper";

interface HeaderUserDropdownProps {
  show: boolean;
  setShow: () => any;
  hideDropdown?: boolean;
  className?: string;
}

const HeaderUserDropdown: FunctionComponent<HeaderUserDropdownProps> = ({
  show,
  setShow,
  hideDropdown = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const authStore = useUnit(authenticationStore);
  const isExpertComptable = authStore?.user?.isExpertComptable;
  const rights = authStore?.user?.rights ?? [];
  const isGroupManager = rights.includes("GESTION_GROUPES");
  const isOfficeManager = rights.includes("GESTION_CABINETS");

  const specificItems = [
    // if isExpertComptable is true, spread in 1-item array; else spread nothing
    ...(isExpertComptable
      ? [
          {
            to: ROUTES.extranet.menu.offices.cgu.generate(),
            text: t("header.categories.offices.cgu"),
          },
        ]
      : []),

    // same pattern for isOfficeManager
    ...(isOfficeManager
      ? [
          {
            to: ROUTES.extranet.menu.offices.users.generate(),
            text: t("header.categories.offices.users"),
          },
        ]
      : []),

    // same pattern for isGroupManager
    ...(isGroupManager
      ? [
          {
            to: ROUTES.extranet.menu.offices.group.generate(),
            text: t("header.categories.offices.group"),
          },
        ]
      : []),
  ];

  const { avatar } = useUserPicture({
    userPictures: authStore?.user?.picture,
  });

  const categories = [
    {
      icon: <UserIcon />,
      title: t("header.categories.account.title"),
      children: [
        {
          to: ROUTES.extranet.menu.account.myInformations.generate(),
          text: t("header.categories.account.informations"),
        },
        {
          to: ROUTES.extranet.menu.account.mySignature.generate(),
          text: t("header.categories.account.personalSignature"),
        },
        {
          to: ROUTES.extranet.menu.account.myPreferences.generate(),
          text: t("header.categories.account.preferences"),
        },
      ],
    },
    {
      icon: <OfficeIcon />,
      title: t("header.categories.offices.title"),
      children: [
        {
          to: ROUTES.extranet.menu.offices.office.generate(),
          text: t("header.categories.offices.office"),
        },
        {
          to: ROUTES.extranet.menu.offices.balanceAndStatistics.generate(),
          text: t("header.categories.offices.balanceAndStatistics"),
        },
      ],
    },
    {
      children: [
        {
          to: ROUTES.extranet.menu.offices.transactionModel.list.generate(),
          text: t("header.categories.offices.transactionModel"),
        },
        {
          to: ROUTES.extranet.menu.offices.mailModel.list.generate(),
          text: t("header.categories.offices.mailModel"),
        },
        {
          to: ROUTES.extranet.menu.offices.contacts.generate(),
          text: t("header.categories.offices.contacts"),
        },
      ],
    },
    {
      children: [
        {
          to: ROUTES.extranet.menu.offices.preferences.generate(),
          text: t("header.categories.offices.preferences"),
        },
        ...specificItems,
      ],
    },
  ];

  const encodedAvatar =
    avatar && !avatar?.base64Data?.includes("base64")
      ? getBase64UrlFrom(avatar.base64Data, avatar.type)
      : avatar?.base64Data;

  return authStore?.user ? (
    <div
      className={`app-dropdown-header dropdown-user ${className} ${hideDropdown ? "cursor-default mr-16" : ""}`}
      onClick={setShow}
    >
      <div className="dropdown-icon">
        <img
          alt={t("header.user")}
          src={encodedAvatar ?? "/img/core/header/icon-user.png"}
          width={32}
          height={32}
        />
      </div>
      <div
        className="dropdown-text"
        data-test={"nav-button"}
        data-alt={`${authStore.user.firstname} ${authStore.user.lastname}`}
      >
        {`${authStore.user.firstname} ${authStore.user.lastname}`}
      </div>
      {!hideDropdown && (
        <div>
          <div className="dropdown-svg">
            <FontAwesomeIcon
              icon={faChevronDown}
              className="dropdown-chevron"
            />
          </div>
          <div className={`dropdown-container ${show ? "" : "hidden"}`}>
            {categories.map((category, index) => (
              <div
                key={`user-menu-category-${index}`}
                className="user-category"
              >
                {category.title && (
                  <div className="category-title">
                    <div className="category-title-icon">{category.icon}</div>
                    <div className="category-title-text">{category.title}</div>
                  </div>
                )}
                {category.children.map((link) => (
                  <Link
                    key={`link_${link.text}`}
                    to={link.to}
                    className="category-link"
                    data-test={"nav-button"}
                    data-alt={link.text}
                  >
                    {link.text}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : (
    <></>
  );
};

export default HeaderUserDropdown;
